import moment from 'moment'
import { merge, isEqual } from 'lodash'
import { CurrencyFormatter } from 'lib/utils/currency'
import { getVerticalName, VEHICLE_INSPECTION_STATUS } from 'lib/utils/store/vehicle'
import { logException } from 'lib/errors'

import {
    Attachments,
    Bos,
    Comment,
    ConfirmedAt,
    Created,
    EVENT_MODULES,
    EventTime,
    Expense,
    InspectionNotices,
    InspectionResult,
    InspectionTrafiData,
    Location,
    Mileage,
    PriceEstimate,
    PurchaseInfo,
    Refill,
    Reminder,
    RemindNext,
    ServiceType,
    Title,
    TrafiEventTime,
    VehicleAdded,
    NewSalesAd,
    AdStatusChange,
    AdTip,
} from './modules'
import {
    EVENT_TYPE,
    SERVICE_EVENT_TYPE,
    SYSTEM_EVENT_ICON_COLORS,
    SYSTEM_EVENT_TYPE,
} from 'lib/utils/store/event'
import { inflect } from 'lib/translate'

const getEventShape = (data = {}) => {
    const now = moment(Date.now()).format('YYYY-MM-DD HH:mm')
    return merge(
        {
            id: null,
            type: null,
            title: '',
            comment: '',
            eventTime: null,
            confirmedAt: null,
            attachments: [],
            createdAt: now,
            updatedAt: now,
            location: {
                latitude: 0,
                longitude: 0,
                locationName: '',
                googlePlaceId: '',
                vicinity: '',
                googlePlaceUrl: '',
            },
            eventData: {
                mileage: '',
                expense: '',
                inspectionResult: '',
                inspectionNotices: '',
                serviceType: '',
                serviceTypeOtherText: '',
            },
            reminders: {
                reminderTopic: '',
                reminderTime: null,
                remindBefore: []
            },
        },
        { ...data }
    )
}

class Event {
    event
    id
    type
    modules
    icon
    color = SYSTEM_EVENT_ICON_COLORS.BLUE

    constructor (event) {
        this.id = event.id
        this.type = event.type
        this.event = event
    }

    setupModules (modules = []) {
        const commonModules = [
            new Created(this.event),
            new EventTime(this.event),
            new ConfirmedAt(this.event),
            new Comment(this.event),
        ]

        this.modules = [...commonModules, ...(modules || [])]
    }

    addModule (module) {
        this.modules = [...this.modules, module]
    }

    canBeDeleted () {
        return true
    }

    isPending () {
        return this.id && !this.getModule('confirmedAt').confirmedAt
    }

    isInPast () {
        return !moment(this.getModule('eventTime').eventTime).isAfter(moment(Date.now()))
    }

    /**
     * Writes all module values to object
     */
    getValuesObject () {
        return this.modules.reduce((object, module) => {
            if (module.writeFieldsToObject) {
                module.writeFieldsToObject(object)
            }
            return object
        }, {})
    }

    getFormValidationSchema (formMode) {
        return this.modules.reduce((object, module) => {
            if (module.writeValidationToObject) {
                module.writeValidationToObject(formMode, object)
            }
            return object
        }, {})
    }

    getModule (name) {
        return this.modules.find(module => module.type === name)
    }

    static create (vehicle) {
        return new Event(vehicle)
    }

    getId () {
        return this.id
    }

    getDateModuleValue (moduleName, format) {
        const dateModule = this.getModule(moduleName)
        return !format
            ? dateModule.value
            : dateModule.getFormattedValue(format)
    }

    getCreated (format) {
        return this.getDateModuleValue(EVENT_MODULES.CREATED, format)
    }

    getEventTime (format) {
        return this.getDateModuleValue(EVENT_MODULES.EVENT_TIME, format)
    }

    getDefaultTitle (t) {
        return t(`events:${this.type}.title`)
    }

    getDisplayTitle (t) {
        const titleModule = this.getModule('title')

        // display title from title module only if module exists and title value provided
        // fall back to translation file title
        return !titleModule || !titleModule.title
            ? this.getDefaultTitle(t)
            : titleModule.title
    }

    getSubTitle () {
        const modulesToDisplay = [EVENT_MODULES.MILEAGE]
        const module = this.modules.find(module => modulesToDisplay.includes(module.type))
        return module && module.getFormattedValue()
    }

    getExtraData1 () {
        const modulesToDisplay = [EVENT_MODULES.EXPENSE]
        const module = this.modules.find(module => modulesToDisplay.includes(module.type))
        return module && module.getFormattedValue()
    }

    getExtraData2 () {
        const modulesToDisplay = []
        const module = this.modules.find(module => modulesToDisplay.includes(module.type))
        return module && module.getValue()
    }

    getIconColor () {
        if (this.isPending()) return SYSTEM_EVENT_ICON_COLORS.ORANGE
        return this.color
    }

    getEventSpecificModuleGroup () {
        return {
            type: 'compactFormGroup',
            className: 'compact-form-group--padded-modules',
            modules: [
                EVENT_MODULES.INSPECTION_TRAFI_DATA,
                EVENT_MODULES.INSPECTION_RESULT,
                EVENT_MODULES.INSPECTION_NOTICES,
                EVENT_MODULES.SERVICE_TYPE,
                EVENT_MODULES.EXPENSE,
                EVENT_MODULES.MILEAGE,
            ],
        }
    }

    getModulesLayoutSchema () {
        return {
            layout: [
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.TITLE,
                    ],
                },
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.EVENT_TIME,
                        EVENT_MODULES.TRAFI_EVENT_TIME,
                        EVENT_MODULES.REMINDER,
                    ],
                },
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.LOCATION,
                    ],
                },
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.ATTACHMENTS,
                    ],
                },
                this.getEventSpecificModuleGroup(),
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.COMMENT,
                    ],
                },
                {
                    type: 'compactFormGroup',
                    className: '',
                    modules: [
                        EVENT_MODULES.REMIND_NEXT,
                    ],
                },
            ]
        }
    }

    /** Utils **/

    isEqual (wrappedEvent) {
        return !wrappedEvent || isEqual(this.event, wrappedEvent.event)
    }
}

export class OtherEvent extends Event {
    constructor (event) {
        super(event)

        this.setupModules([
            new Title(event),
            new Expense(event),
            new Mileage(event),
            new Attachments(event),
            new Location(event),
            new Reminder(event),
            new RemindNext(event),
        ])

        this.icon = 'lightbulb'
    }

    static create (event) {
        const eventShape = getEventShape()
        return new OtherEvent({
            ...eventShape,
            ...event,
            eventData: {
                ...eventShape.eventData,
                ...event.eventData,
            },
            reminders: {
                ...eventShape.reminders,
                ...event.reminders,
            },
        })
    }
}

export class RefillEvent extends Event {
    constructor (event) {
        super(event)

        this.setupModules([
            new Mileage(event),
            new Attachments(event),
            new Location(event),
            new Refill(event),
        ])

        this.icon = 'gas-pump'
    }

    static create (event) {
        return new RefillEvent(getEventShape(event))
    }

    hasValuesForConsumptionCalculation () {
        const refillModule = this.getModule(EVENT_MODULES.REFILL)
        return refillModule.fuelAmount && refillModule.fuelPrice && refillModule.fuelTotalPrice
    }

    getFuelAmount (emptyValue = '- l') {
        const refillModule = this.getModule(EVENT_MODULES.REFILL)
        return refillModule.fuelAmount ? `${refillModule.fuelAmount} l` : emptyValue
    }

    getFuelPrice (emptyValue = '- €/l') {
        const refillModule = this.getModule(EVENT_MODULES.REFILL)
        return refillModule.fuelPrice ? `${CurrencyFormatter.format(refillModule.fuelPrice)}/l` : emptyValue
    }

    getFuelTotalPrice (emptyValue = '-') {
        const refillModule = this.getModule(EVENT_MODULES.REFILL)
        return refillModule.fuelTotalPrice ? `${CurrencyFormatter.format(refillModule.fuelTotalPrice)}` : emptyValue
    }

    getExtraData1 () {
        return this.getFuelTotalPrice(null)
    }

    getExtraData2 () {
        return `${this.getFuelAmount()} / ${this.getFuelPrice()}`
    }

    getSubTitle () {
        const mileageModule = this.getModule(EVENT_MODULES.MILEAGE)
        return mileageModule.mileage ? `${mileageModule.mileage} km` : null
    }

    getEventSpecificModuleGroup () {
        return {
            type: 'compactFormGroup',
            className: '',
            modules: [
                EVENT_MODULES.REFILL,
                EVENT_MODULES.MILEAGE,
            ],
        }
    }
}

export class InspectionEvent extends Event {
    constructor (event) {
        super(event)

        this.setupModules([
            new TrafiEventTime(event),
            new Mileage(event),
            new Expense(event),
            new Attachments(event),
            new InspectionTrafiData(event),
            new InspectionResult(event),
            new InspectionNotices(event),
            new Location(event),
            new Reminder(event),
            new RemindNext(event),
        ])

        this.icon = 'file-check'
    }

    static create (event) {
        return new InspectionEvent(getEventShape(event))
    }

    getIconColor () {
        if (this.isPending()) return SYSTEM_EVENT_ICON_COLORS.ORANGE

        return this.getInspectionResult() === VEHICLE_INSPECTION_STATUS.ACCEPTED ? SYSTEM_EVENT_ICON_COLORS.GREEN : SYSTEM_EVENT_ICON_COLORS.RED
    }

    getInspectionResult () {
        const trafiDataModule = this.getModule(EVENT_MODULES.INSPECTION_TRAFI_DATA)
        const inspectionResultModule = this.getModule(EVENT_MODULES.INSPECTION_RESULT)

        let inspectionResult

        if (trafiDataModule && trafiDataModule.trafiInspectionResult) {
            inspectionResult = trafiDataModule.trafiInspectionResult
        } else if (inspectionResultModule && inspectionResultModule.inspectionResult) {
            inspectionResult = inspectionResultModule.inspectionResult
        }

        return inspectionResult
    }

    getTrafiInspectionResult () {
        const module = this.getModule(EVENT_MODULES.INSPECTION_TRAFI_DATA)
        return module && module.trafiInspectionResult
    }
}

export class ServiceEvent extends Event {
    constructor (event) {
        super(event)

        this.setupModules([
            new Mileage(event),
            new Expense(event),
            new Attachments(event),
            new ServiceType(event),
            new Location(event),
            new Reminder(event),
            new RemindNext(event),
        ])

        this.icon = 'wrench'
    }

    static create (event) {
        return new ServiceEvent(getEventShape(event))
    }

    getDisplayTitle (t) {
        const serviceTypeModule = this.getModule(EVENT_MODULES.SERVICE_TYPE)

        let title = null

        if (serviceTypeModule) {
            if (serviceTypeModule.serviceType === SERVICE_EVENT_TYPE.OTHER) {
                title = serviceTypeModule.serviceTypeOtherText
            } else {
                title = (
                    serviceTypeModule.serviceType &&
                    t(`events:serviceType.${serviceTypeModule.serviceType}`)
                )
            }
        }

        return title || Event.prototype.getDisplayTitle.call(this, t)
    }
}

export class SystemEvent extends Event {
    constructor (event) {
        super(event)

        this.systemType = event.eventData.systemType
        this.systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null

        this.setupModules([
            new Location(event)
        ])

        this.icon = ['far', 'cog']

        switch (this.systemType) {
        case SYSTEM_EVENT_TYPE.BOS_VEHICLE_BOUGHT:
            this.icon = 'star'
            this.addModule(new Bos(event))
            break
        case SYSTEM_EVENT_TYPE.BOS_VEHICLE_SOLD:
            this.icon = ['fas', 'handshake-alt']
            this.addModule(new Bos(event))
            break
        case SYSTEM_EVENT_TYPE.VEHICLE_PURCHASED:
            this.icon = 'star'
            this.addModule(new PurchaseInfo(event))
            break
        case SYSTEM_EVENT_TYPE.PRICE_ESTIMATE:
            this.icon = 'tag'
            this.addModule(new PriceEstimate(event))
            break
        case SYSTEM_EVENT_TYPE.VEHICLE_ADDED:
            this.icon = 'star'
            this.addModule(new VehicleAdded(event))
            break
        case SYSTEM_EVENT_TYPE.NEW_SALES_AD:
            this.icon = 'tag'
            this.addModule(new AdTip(event))
            this.addModule(new NewSalesAd(event))
            break
        case SYSTEM_EVENT_TYPE.AD_STATUS_CHANGE:
            this.icon = 'tag'
            this.addModule(new AdTip(event))
            this.addModule(new AdStatusChange(event))
            break
        }
    }

    static create (event) {
        return new SystemEvent(getEventShape(event))
    }

    getIconColor () {
        switch (this.systemType) {
        case SYSTEM_EVENT_TYPE.BOS_VEHICLE_BOUGHT:
        case SYSTEM_EVENT_TYPE.BOS_VEHICLE_SOLD:
        case SYSTEM_EVENT_TYPE.VEHICLE_PURCHASED:
        case SYSTEM_EVENT_TYPE.VEHICLE_ADDED:
            return SYSTEM_EVENT_ICON_COLORS.GREEN
        case SYSTEM_EVENT_TYPE.NEW_SALES_AD:
        case SYSTEM_EVENT_TYPE.AD_STATUS_CHANGE:
            return SYSTEM_EVENT_ICON_COLORS.ORANGE
        case SYSTEM_EVENT_TYPE.PRICE_ESTIMATE:
            return SYSTEM_EVENT_ICON_COLORS.BLUE
        default:
            return SYSTEM_EVENT_ICON_COLORS.BLUE
        }
    }

    getSubTitle (t) {
        let price
        let mileage

        try {
            const data = this.systemEventData

            switch (this.systemType) {
            case SYSTEM_EVENT_TYPE.BOS_VEHICLE_BOUGHT:
                return t('events:SYSTEM.BOS_VEHICLE_BOUGHT.subTitle')
            case SYSTEM_EVENT_TYPE.BOS_VEHICLE_SOLD:
                return t('events:SYSTEM.BOS_VEHICLE_SOLD.subTitle')
            case SYSTEM_EVENT_TYPE.PRICE_ESTIMATE:
                price = CurrencyFormatter.format(data.price)
                mileage = data.mileage.toLocaleString('fi-FI')
                return `${price} / ${mileage} km`
            }
        } catch (e) {
            logException(e)
            return null
        }

        return Event.prototype.getSubTitle.call(this)
    }

    getDisplayTitle (t) {
        if (this.systemType === SYSTEM_EVENT_TYPE.NEW_SALES_AD) {
            const { vertical, isActive } = this.getModule(EVENT_MODULES.NEW_SALES_AD)

            if (isActive) {
                const verticalName = inflect(t, getVerticalName(t, vertical), 'pronoun')
                return t(`events:${this.type}.${this.systemType}.titleActive`, { vertical: verticalName })
            } else {
                const verticalName = inflect(t, getVerticalName(t, vertical), 'pronoun')
                return t(`events:${this.type}.${this.systemType}.title`, { vertical: verticalName })
            }
        }

        let module

        switch (this.systemType) {
        case SYSTEM_EVENT_TYPE.NEW_SALES_AD:
            module = this.getModule(EVENT_MODULES.NEW_SALES_AD)

            if (module.isActive) {
                const verticalName = inflect(t, getVerticalName(t, module.vertical), 'pronoun')
                return t(`events:${this.type}.${this.systemType}.titleActive`, { vertical: verticalName })
            } else {
                const verticalName = inflect(t, getVerticalName(t, module.vertical), 'illative')
                return t(`events:${this.type}.${this.systemType}.title`, { vertical: verticalName })
            }
        case SYSTEM_EVENT_TYPE.AD_STATUS_CHANGE:
            module = this.getModule(EVENT_MODULES.AD_STATUS_CHANGE)
            return t(`events:${this.type}.${this.systemType}.${module.adStatus.toLowerCase()}.title`)
        }

        return t(`events:${this.type}.${this.systemType}.title`)
    }

    canBeDeleted () {
        return ![
            SYSTEM_EVENT_TYPE.VEHICLE_ADDED,
            SYSTEM_EVENT_TYPE.BOS_VEHICLE_BOUGHT,
            SYSTEM_EVENT_TYPE.BOS_VEHICLE_SOLD,
            SYSTEM_EVENT_TYPE.VEHICLE_PURCHASED,
        ].includes(this.systemType)
    }
}

export const wrapEvent = (event, type = null) => {
    event = {
        type,
        ...event
    }
    switch (event.type) {
    case EVENT_TYPE.OTHER:
        return Object.freeze(OtherEvent.create(event))
    case EVENT_TYPE.INSPECTION:
        return Object.freeze(InspectionEvent.create(event))
    case EVENT_TYPE.SERVICE:
        return Object.freeze(ServiceEvent.create(event))
    case EVENT_TYPE.REFILL:
        return Object.freeze(RefillEvent.create(event))
    case EVENT_TYPE.SYSTEM:
        return Object.freeze(SystemEvent.create(event))
    default:
        throw new Error(`Invalid event type: ${event.type}`)
    }
}
